import { motion } from "framer-motion";
import { Flame, LineChart } from "lucide-react";
import { SITE, IMAGES } from "@/data/site";
import { EmberCanvas } from "@/components/EmberCanvas";
import { CopyContract } from "@/components/CopyContract";

export const Hero = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden grain pt-24 pb-16"
      data-testid="hero-section"
    >
      {/* Background layers */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-black" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-ember/20 blur-[140px] rounded-full" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blood/15 blur-[120px] rounded-full" />
        <div className="absolute top-1/3 left-0 w-[400px] h-[400px] bg-solana/10 blur-[120px] rounded-full" />
      </div>
      <EmberCanvas className="absolute inset-0 z-10 w-full h-full" density={70} />

      <div className="relative z-20 max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center w-full">
        {/* Left: copy */}
        <div className="order-2 lg:order-1 text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="font-body text-xs font-semibold tracking-widest uppercase text-gray-200">
              Live on Solana • Pump.fun
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.9] uppercase text-white"
          >
            The <span className="fire-gradient-text text-glow-ember">Greater</span>
            <br /> Bull
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-6 font-body text-lg md:text-xl text-gray-300 max-w-lg mx-auto lg:mx-0 leading-relaxed"
          >
            {SITE.tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start"
          >
            <a
              href={SITE.links.pumpfun}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-buy-button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-black font-body font-extrabold text-base px-8 py-4 rounded-md transition-transform duration-300 hover:scale-105 glow-gold uppercase tracking-wide"
            >
              <Flame className="w-5 h-5" /> Buy on Pump.fun
            </a>
            <a
              href={SITE.links.dexscreener}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-chart-button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-blood text-white hover:bg-blood font-body font-bold text-base px-8 py-4 rounded-md transition-colors duration-300 uppercase tracking-wide"
            >
              <LineChart className="w-5 h-5" /> View Chart
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-8 flex justify-center lg:justify-start"
          >
            <CopyContract testId="hero-copy-contract" />
          </motion.div>
        </div>

        {/* Right: mascot */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="order-1 lg:order-2 flex justify-center"
        >
          <div className="relative animate-float">
            <div className="absolute inset-0 bg-ember/40 blur-[80px] rounded-full scale-90" />
            <img
              src={IMAGES.heroBull}
              alt="The Greater Bull flaming mascot"
              className="relative z-10 w-[280px] sm:w-[380px] lg:w-[500px] object-contain drop-shadow-[0_0_45px_rgba(255,69,0,0.5)]"
              data-testid="hero-bull-image"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
